import React, { useCallback } from "react";
import Link from "next/link";
import styled from "styled-components";
import Logo from "../../assets/images/layout/logo.svg";
import { useLenis } from "../../utils/LenisContext";
import { SITE_URL } from "../../utils/constants";

type HeaderProps = {
  hasLoaded: boolean;
  headerTitle: string;
  isOpen: boolean;
  closeNav: () => void;
  locale: string | undefined;
  route: string;
  /**
   * Landing-page mode: renders the logo as plain brand mark (no link,
   * no scroll-to-top). Use for conversion-focused pages where the only
   * escape route is the footer link to acueducto.com.
   */
  unlinkLogo?: boolean;
};

function Header({ hasLoaded, headerTitle, isOpen, closeNav, locale, route, unlinkLogo }: HeaderProps) {
  const { lenis } = useLenis();

  const backUp = useCallback((e: React.MouseEvent) => {
    closeNav();
    if (route === "/" || route === "/eng") {
      e.preventDefault();
      lenis?.scrollTo(0, { immediate: false });
    }
  }, [closeNav, route, lenis]);

  return (
    <TopHeader
      $reveal={hasLoaded}
      className="w-full short-viewport:w-[calc(100%-36px)] pt-[3.5rem] pl-[40px] short-viewport:pl-[20px] [@media(width>768px)_and_(height>700px)]:pt-[5.5rem] [@media(width>768px)_and_(height>700px)]:pl-[6rem] [@media(width>1530px)_and_(height>700px)]:pt-[6rem] [@media(width>1530px)_and_(height>700px)]:pl-[5rem]"
    >
      {unlinkLogo ? (
        <LogoLink as="div" aria-label="acueducto" style={{ cursor: "default", pointerEvents: "none" }}>
          <span className="sr-only">acueducto</span>
          <Logo />
        </LogoLink>
      ) : (
        <LogoLink as={Link} href={`${SITE_URL}${locale === "es" ? "/es" : ""}`} locale={locale} onClick={backUp}>
          <span className="sr-only">acueducto</span>
          <Logo />
        </LogoLink>
      )}
      <HeaderTitle className="top-[61px] short-viewport:top-[40px]" $hide={isOpen}>{headerTitle}</HeaderTitle>
    </TopHeader>
  );
}

export default React.memo(Header);

const HeaderTitle = styled.div<{ $hide: boolean }>`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  text-transform: uppercase;
  font-size: 1.4rem;
  letter-spacing: 4px;
  z-index: 2;
  mix-blend-mode: exclusion;
  opacity: ${(props) => (props.$hide ? 0 : 1)};
  transition: opacity 0.2s ease;
`;

const TopHeader = styled.header<{ $reveal: boolean }>`
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding-right: 80px;
  padding-bottom: 60px;
  z-index: 50;
  margin: 0px auto;
  max-width: 1500px;
  pointer-events: none;
  mix-blend-mode: exclusion;
  opacity: ${(props) => (props.$reveal ? 1 : 0)};
  transition: opacity 0.3s ease-out 0.15s;
  @media (max-width: 800px) {
    flex-direction: column;
    ${HeaderTitle} {
      transform: none;
      left: 0;
      position: relative;
      top: 0;
    }
  }
  @media (max-width: 600px) {
    ${HeaderTitle} {
      font-size: 1.2rem;
    }
  }
`;

//Aqui

const LogoLink = styled.div`
  display: flex;
  grid-column: 1 / span 2;
  max-width: 130px;
  margin: 0;
  cursor: pointer;
  color: inherit;
  text-decoration: none;
  pointer-events: auto;
  span {
    font-size: 0;
  }
  svg {
    max-width: 130px;
    max-height: 18px;
    width: 100%;
    path {
      fill: ${(props) => props.theme.colors.white};
    }
  }
`;
